<script lang="ts">
  import { initials } from '../utils/geo';
  import { photoUrl } from '../utils/photos';
  import { t } from '../i18n/index.ts';

  // Avatar is a member's photo, or their initials on their color, with the
  // green current dot and a battery chip when asked (spec 010 FR-003).
  interface Props {
    name: string;
    userId: string;
    color: string;
    photo?: string;
    size?: number;
    dot?: boolean;
    dim?: boolean;
    battery?: number;
    charging?: boolean;
  }
  let { name, userId, color, photo, size = 44, dot = false, dim = false, battery, charging = false }: Props = $props();

  let src: string | null = $state(null);
  $effect(() => {
    src = null;
    if (!photo) return;
    let live = true;
    photoUrl(userId, photo).then((u) => { if (live) src = u; });
    return () => { live = false; };
  });

  const low = $derived(battery != null && battery <= 20 && !charging);
</script>

<!-- The photo or initials are decoration: the member's name is always beside them. -->
<span class="avatar" style="width: {size}px; height: {size}px; opacity: {dim ? 0.55 : 1};">
  {#if src}
    <img {src} alt="" class="avatar-face" />
  {:else}
    <span class="avatar-face" style="background: {color}; font-size: {Math.round(size * 0.36)}px;" aria-hidden="true">{initials(name)}</span>
  {/if}
  {#if dot}<span class="avatar-dot" title={t('componentsAvatarCurrent')} aria-hidden="true"></span>{/if}
  {#if battery != null}
    <span class="avatar-battery" class:low title={t('componentsAvatarBattery', { level: battery, charging: charging ? 'yes' : 'no' })}>
      <i aria-hidden="true" class="las {charging ? 'la-bolt' : 'la-battery-half'}"></i><span class="sr-only">{t('componentsAvatarBattery', { level: battery, charging: charging ? 'yes' : 'no' })}</span><span aria-hidden="true">{t('componentsAvatarBatteryLevel', { level: battery })}</span>
    </span>
  {/if}
</span>

<style>
  .avatar {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
  }
  .avatar-face {
    width: 100%;
    height: 100%;
    border-radius: 9999px;
    object-fit: cover;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
  }
  .avatar-dot {
    position: absolute;
    top: 0;
    right: 0;
    width: 28%;
    height: 28%;
    border-radius: 9999px;
    background: var(--theme-success);
    border: 2px solid var(--theme-mantle);
  }
  .avatar-battery {
    position: absolute;
    left: 50%;
    bottom: -0.55rem;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 0.1rem;
    padding: 0 0.3rem;
    border-radius: 0.5rem;
    font-size: 0.65rem;
    font-weight: 600;
    white-space: nowrap;
    background: var(--theme-base);
    color: var(--theme-text);
    border: 1px solid var(--theme-surface1);
  }
  .avatar-battery.low {
    color: var(--theme-error);
  }
</style>
